<template>
    <div class="modal-overlay">
        <div class="modal-content">
            <h2>Add New Goal</h2>
            <form @submit.prevent="handleSubmit">
                <div class="form-group">
                    <label for="goal-type">Goal Type</label>
                    <select 
                        id="goal-type"
                        v-model="form.goal_type_id"
                        required
                    >
                        <option value="">Select a type</option>
                        <option 
                            v-for="(name, id) in goalTypes" 
                            :key="id" 
                            :value="Number(id)"
                        >
                            {{ name }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="year">Year</label>
                    <select 
                        id="year"
                        v-model="form.year"
                        required
                    >
                        <option 
                            v-for="year in availableYears" 
                            :key="year" 
                            :value="year"
                        >
                            {{ year }}
                        </option>
                    </select>
                </div>

                <div class="form-group">
                    <label for="title">Title</label>
                    <input 
                        id="title"
                        v-model="form.title"
                        type="text"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="description">Description</label>
                    <textarea 
                        id="description"
                        v-model="form.description"
                        rows="3"
                    ></textarea>
                </div>

                <div class="form-group">
                    <label for="target-date">Target Date</label>
                    <input 
                        id="target-date"
                        v-model="form.target_date"
                        type="date"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="target-value">Target Value/Amount</label>
                    <input 
                        id="target-value"
                        v-model="form.target_value"
                        type="number"
                        step="0.01"
                        min="0"
                    />
                </div>

                <div class="modal-actions">
                    <button type="button" class="cancel-btn" @click="handleClose">
                        Cancel
                    </button>
                    <button type="submit" class="save-btn">
                        Save Goal
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';
import { format, parseISO } from 'date-fns';

export default {
    name: 'GoalModal',
    props: {
        goalTypes: {
            type: Object,
            required: true
        },
        editing: {
            type: Boolean,
            default: false
        },
        initialGoal: {
            type: Object,
            default: null
        },
        duplicatingGoal: {
            type: Object,
            default: null
        }
    },
    emits: ['close', 'save'],
    setup(props, { emit }) {
        const currentYear = new Date().getFullYear();
        
        // Generate array of years: current year + 2 years ahead
        const availableYears = computed(() => {
            return [
                currentYear,
                currentYear + 1,
                currentYear + 2
            ];
        });

        const formatDateForInput = (dateString) => {
            try {
                return format(parseISO(dateString), 'yyyy-MM-dd');
            } catch (error) {
                console.error('Error formatting date:', error);
                return '';
            }
        };

        const form = ref({
            goal_type_id: '',
            title: '',
            description: '',
            target_date: '',
            year: currentYear,
            target_value: '',
            success_criteria: '',
            status: 'in_progress'
        });

        // If editing or duplicating, populate form with initial goal data
        if ((props.editing || props.duplicatingGoal) && (props.initialGoal || props.duplicatingGoal)) {
            const sourceGoal = props.duplicatingGoal || props.initialGoal;
            const title = sourceGoal.title;
            // Only append (Copy) if it's not already there
            const copyAppendix = props.duplicatingGoal ? (title.endsWith('(Copy)') ? '' : ' (Copy)') : '';
            
            form.value = {
                goal_type_id: sourceGoal.goal_type_id,
                year: sourceGoal.year,
                title: title + copyAppendix,
                description: sourceGoal.description,
                target_date: formatDateForInput(sourceGoal.target_date),
                target_value: sourceGoal.target_value || '',
                success_criteria: sourceGoal.success_criteria || '',
                status: 'in_progress'  // Always start a duplicated goal as in_progress
            };
        }

        // Reset form when modal is closed
        const resetForm = () => {
            form.value = {
                goal_type_id: '',
                title: '',
                description: '',
                target_date: '',
                year: currentYear,
                target_value: '',
                success_criteria: '',
                status: 'in_progress'
            };
        };

        const handleSubmit = () => {
            emit('save', { ...form.value });
        };

        const handleClose = () => {
            resetForm();
            emit('close');
        };

        return {
            form,
            handleSubmit,
            handleClose,
            availableYears
        };
    }
};
</script>

<style scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 24px;
    border-radius: 8px;
    width: 90%;
    max-width: 500px;
}

.form-group {
    margin-bottom: 16px;
}

label {
    display: block;
    margin-bottom: 8px;
    color: #2c3e50;
}

input, select, textarea {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

.modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 24px;
}

.cancel-btn {
    padding: 8px 16px;
    border: 1px solid #ddd;
    background: white;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn {
    padding: 8px 16px;
    background: #4285f4;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
}

.save-btn:hover {
    background: #1976d2;
}

/* Make year dropdown same width as other inputs but not too wide */
select#year {
    width: auto;
    min-width: 100px;
}
</style> 